(function () {
  "use strict";

  try {
    // Consent Mode defaults are set inline in theme.liquid <head> before this
    // file loads (so denied-by-default applies even while this asset downloads).
    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function () {
        window.dataLayer.push(arguments);
      };

    const deriveFlags = categoryStatus => {
      const analyticsAllowed = Boolean(categoryStatus.Analytics);
      const functionalAllowed = Boolean(categoryStatus.Functional);
      const marketingAllowed = Boolean(categoryStatus.Marketing || categoryStatus.Advertising);

      return { analyticsAllowed, functionalAllowed, marketingAllowed };
    };

    const TRACKING_STATE_KEY = "zinus_cmp_tracking_state";

    const getRuleSignature = () => {
      const rules = Array.isArray(window.RULES) ? window.RULES : [];
      const names = [
        ...new Set(
          rules
            .filter(rule => rule && typeof rule.n === "string")
            .map(rule => rule.n),
        ),
      ].sort();
      return names.length > 0 ? JSON.stringify(names) : "";
    };

    const hasPersistedSecuvyDecision = () => {
      try {
        const raw = localStorage.getItem("secuvy");
        if (!raw) return false;

        const parsed = JSON.parse(decodeURIComponent(raw));
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return false;

        const config = window.klaro?.defaultConfig || window.klaroConfig || {};
        const necessaryPurpose = String(
          config.translations?.en?.necessaryText || "",
        ).toLowerCase();
        const configuredNames = (Array.isArray(config.services) ? config.services : [])
          .filter(service => {
            if (!service || typeof service.name !== "string" || service.required === true) {
              return false;
            }
            const purposes = Array.isArray(service.purposes) ? service.purposes : [];
            return !purposes.some(purpose => {
              const normalized = String(purpose).toLowerCase();
              return (
                normalized === "necessary" ||
                (necessaryPurpose && normalized === necessaryPurpose)
              );
            });
          })
          .map(service => service.name);

        const rules = Array.isArray(window.RULES) ? window.RULES : [];
        const ruleNames = [
          ...new Set(
            rules
              .filter(rule => rule && typeof rule.n === "string")
              .map(rule => rule.n),
          ),
        ];
        const requiredNames = configuredNames.length > 0 ? configuredNames : ruleNames;

        return (
          requiredNames.length > 0 &&
          requiredNames.every(name => typeof parsed[name] === "boolean")
        );
      } catch (err) {
        return false;
      }
    };

    const clearTrackingState = () => {
      try {
        localStorage.removeItem(TRACKING_STATE_KEY);
      } catch (err) {}
    };

    const persistTrackingState = (analyticsAllowed, marketingAllowed) => {
      try {
        const raw = localStorage.getItem("secuvy");
        if (!raw) {
          clearTrackingState();
          return false;
        }

        const parsed = JSON.parse(decodeURIComponent(raw));
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          clearTrackingState();
          return false;
        }
        const ruleSignature = getRuleSignature();
        if (!ruleSignature) {
          clearTrackingState();
          return false;
        }

        localStorage.setItem(
          TRACKING_STATE_KEY,
          JSON.stringify({
            secuvy: raw,
            rules: ruleSignature,
            analytics: Boolean(analyticsAllowed),
            marketing: Boolean(marketingAllowed),
          }),
        );
        return true;
      } catch (err) {
        clearTrackingState();
        return false;
      }
    };

    let pendingTrackingState = null;
    let consentReloadFallback = null;

    const reloadPageOnce = () => {
      if (
        window.__zinusConsentReloaded ||
        !window.location ||
        typeof window.location.reload !== "function"
      ) {
        return;
      }
      window.__zinusConsentReloaded = true;
      window.__zinusRebuyDeclineReloaded = true;
      window.location.reload();
    };

    const scheduleConsentReload = () => {
      window.__zinusConsentReloadNeeded = true;
      if (consentReloadFallback !== null) return;
      consentReloadFallback = setTimeout(reloadPageOnce, 2500);
    };

    window.addEventListener("SecuvyConsentPersisted", () => {
      const bootTrackingAllowed = window.__zinusTrackingConsentGranted === true;
      let nextTrackingAllowed = bootTrackingAllowed;
      let persisted = false;

      if (pendingTrackingState) {
        nextTrackingAllowed = Boolean(
          pendingTrackingState.analyticsAllowed || pendingTrackingState.marketingAllowed,
        );
        persisted = persistTrackingState(
          pendingTrackingState.analyticsAllowed,
          pendingTrackingState.marketingAllowed,
        );
      }

      if (
        window.__zinusConsentReloadNeeded ||
        (persisted && bootTrackingAllowed !== nextTrackingAllowed)
      ) {
        reloadPageOnce();
      }
    });

    const applyGoogleConsent = (analyticsAllowed, marketingAllowed) => {
      if (typeof window.gtag !== "function") return;

      window.gtag("consent", "update", {
        analytics_storage: analyticsAllowed ? "granted" : "denied",
        ad_storage: marketingAllowed ? "granted" : "denied",
        ad_user_data: marketingAllowed ? "granted" : "denied",
        ad_personalization: marketingAllowed ? "granted" : "denied",
      });
    };

    const applyVWOConsent = analyticsAllowed => {
      try {
        window._vwo_q = window._vwo_q || [];

        if (analyticsAllowed) {
          window._vwo_pc_custom = {
            a: 1,
            t: 1,
          };

          window._vwo_q.push(["track.consent.granted"]);
        } else {
          window._vwo_q.push(["track.consent.denied"]);
          window._vwo_q.push(["optOutVisitor"]);
        }
      } catch (err) {
        console.error("Error applying VWO consent:", err);
      }
    };

    const applyFacebookConsent = marketingAllowed => {
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "secuvy_facebook_consent_update",
          secuvy_facebook_marketing: marketingAllowed ? "granted" : "denied",
        });
      } catch (err) {
        console.error("Error applying Facebook consent:", err);
      }
    };

    const applyBingConsent = marketingAllowed => {
      try {
        window.uetq = window.uetq || [];
        window.uetq.push("consent", "update", {
          ad_storage: marketingAllowed ? "granted" : "denied",
        });
      } catch (err) {
        console.error("Error applying Bing consent:", err);
      }
    };

    const applyPinterestConsent = marketingAllowed => {
      try {
        window.pintrk =
          window.pintrk ||
          function () {
            window.pintrk.queue.push(arguments);
          };

        window.pintrk.queue = window.pintrk.queue || [];

        window.pintrk("setconsent", Boolean(marketingAllowed));
      } catch (err) {
        console.error("Error applying Pinterest consent:", err);
      }
    };

    const applyKlaviyoIdentify = marketingAllowed => {
      try {
        if (!marketingAllowed) {
          window.__zinusKlaviyoIdentified = false;
          return;
        }

        if (window.__zinusKlaviyoIdentified) return;

        const payloadEl = document.getElementById("zinus-klaviyo-identify-payload");
        if (!payloadEl) return;

        let email = "";
        try {
          email = JSON.parse((payloadEl.textContent || "").trim());
        } catch (parseErr) {
          return;
        }

        if (!email || typeof email !== "string") return;

        window._learnq = window._learnq || [];
        window._learnq.push(["identify", { "$email": email }]);
        window.__zinusKlaviyoIdentified = true;
      } catch (err) {
        console.error("Error applying Klaviyo identify:", err);
      }
    };

    const bvTrackingAllowed = () => {
      if (window.__secuvyConsent && typeof window.__secuvyConsent.analytics === "boolean") {
        return window.__secuvyConsent.analytics;
      }

      try {
        if (!hasPersistedSecuvyDecision()) return false;

        const raw = localStorage.getItem("secuvy");
        if (!raw) return false;

        const parsed = JSON.parse(decodeURIComponent(raw));
        if (typeof parsed.Analytics === "boolean") return parsed.Analytics;
        if (typeof parsed.analytics === "boolean") return parsed.analytics;
      } catch (err) {}

      return false;
    };

    const applyBazaarvoiceConsent = analyticsAllowed => {
      try {
        function applyWhenReady() {
          if (!window.BV || !window.BV.cookieConsent) {
            setTimeout(applyWhenReady, 300);
            return;
          }

          if (analyticsAllowed) {
            document.cookie = "NoCookie=; Max-Age=0; Path=/; SameSite=Lax";
            document.cookie = "NoCookie=; Max-Age=0; Path=/; Domain=.zinus.com; SameSite=Lax";
          } else if (window.BV.pixel && typeof window.BV.pixel.doNotTrack === "function") {
            window.BV.pixel.doNotTrack();
          }

          window.BV.cookieConsent.setConsent({
            BVBRANDID: Boolean(analyticsAllowed),
            BVBRANDSID: Boolean(analyticsAllowed),
          });
        }

        applyWhenReady();
      } catch (err) {
        console.error("Error applying Bazaarvoice consent:", err);
      }
    };

    window.bvCallback = function () {
      applyBazaarvoiceConsent(bvTrackingAllowed());
    };

    // 2026-07: applyRebuyConsent removed. Rebuy loads via the Shopify app embed
    // and is governed by the generic Secuvy RULES pipeline (categorized
    // Necessary in the CMP), like every other third-party script.

    const applyShopifyConsent = (analyticsAllowed, marketingAllowed, functionalAllowed) => {
      try {
        const shopify = window.Shopify;
        const customerPrivacy = shopify && shopify.customerPrivacy;

        if (!customerPrivacy || typeof customerPrivacy.setTrackingConsent !== "function") {
          return;
        }

        customerPrivacy.setTrackingConsent({
          analytics: analyticsAllowed,
          marketing: marketingAllowed,
          preferences: functionalAllowed,
          sale_of_data: marketingAllowed,
        });
      } catch (err) {
        console.error("Error applying Shopify consent:", err);
      }
    };

    window.addEventListener("SecuvyConsentStatus", ev => {
      try {
        const detail = ev.detail || {};
        const hasExplicitUserAction = detail.source === "user";
        const hasCompletePersistedDecision = hasPersistedSecuvyDecision();
        // 2026-07: trust the CMP's reported category status directly. Regional
        // consent policy (implied consent / opt-out for US) lives in the Secuvy
        // config; zeroing the status here forced opt-in for all fresh visitors.
        // Explicit user choices and persisted decisions flow through unchanged.
        const categoryStatus = detail.categoryStatus || {};
        const { analyticsAllowed, marketingAllowed, functionalAllowed } =
          deriveFlags(categoryStatus);
        const previousConsent = window.__secuvyConsent || {
          analytics: window.__zinusAnalyticsConsentGranted === true,
          functional: false,
          marketing: window.__zinusMarketingConsentGranted === true,
        };
        const consentRevoked =
          (previousConsent.analytics === true && !analyticsAllowed) ||
          (previousConsent.functional === true && !functionalAllowed) ||
          (previousConsent.marketing === true && !marketingAllowed);

        pendingTrackingState = { analyticsAllowed, marketingAllowed };

        if (hasExplicitUserAction && consentRevoked) {
          clearTrackingState();
          scheduleConsentReload();
        } else if (!hasExplicitUserAction && !hasCompletePersistedDecision) {
          clearTrackingState();
          if (window.__zinusTrackingConsentGranted === true) reloadPageOnce();
        } else if (!hasExplicitUserAction && hasCompletePersistedDecision) {
          const nextTrackingAllowed = Boolean(analyticsAllowed || marketingAllowed);
          const persisted = persistTrackingState(analyticsAllowed, marketingAllowed);
          if (
            persisted &&
            (window.__zinusTrackingConsentGranted === true) !== nextTrackingAllowed
          ) {
            reloadPageOnce();
          }
        }

        window.__secuvyConsent = {
          analytics: analyticsAllowed,
          functional: functionalAllowed,
          marketing: marketingAllowed,
        };
        applyGoogleConsent(analyticsAllowed, marketingAllowed);
        applyVWOConsent(analyticsAllowed);
        applyFacebookConsent(marketingAllowed);
        applyBingConsent(marketingAllowed);
        applyPinterestConsent(marketingAllowed);
        applyShopifyConsent(analyticsAllowed, marketingAllowed, functionalAllowed);
        applyKlaviyoIdentify(marketingAllowed);

        // Bazaarvoice display widgets remain available; pixel identity and cookies follow analytics consent.
        applyBazaarvoiceConsent(analyticsAllowed);

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "secuvy_consent_update",
          secuvy_analytics_consent: analyticsAllowed ? "granted" : "denied",
          secuvy_marketing_consent: marketingAllowed ? "granted" : "denied",
        });
      } catch (err) {
        console.error("Error handling SecuvyConsentStatus:", err);
      }
    });
  } catch (err) {
    console.error("Error initializing vendor consent bridge:", err);
  }
})();
